import type { AIActivity } from '../types/chat.types';

/**
 * Utilities for mapping stream events to AI activity states
 */

/**
 * Maps OpenAI stream event types to appropriate AI activity states
 * @param eventType - The type of stream event received
 * @param hasSearched - Whether any retrieval/search tool has been used in this response
 * @param eventData - Raw event payload string for tool-specific disambiguation
 * @returns The appropriate AIActivity state
 */
export const getActivityFromEvent = (
  eventType: string,
  hasSearched: boolean,
  eventData?: string,
): AIActivity => {
  // To avoid multiple switch case for web_search_call events, we can only check the event prefix.
  if (eventType.startsWith('response.web_search_call.')) {
    return eventType === 'response.web_search_call.completed'
      ? 'analyzing'
      : 'web_searching';
  }

  if (eventType.startsWith('response.function_call_arguments.')) {
    return eventType === 'response.function_call_arguments.done'
      ? 'analyzing'
      : 'function_calling';
  }

  if (eventType.startsWith('response.reasoning_summary_text.')) {
    return eventType === 'response.reasoning_summary_text.done'
      ? 'thinking'
      : 'reasoning';
  }

  switch (eventType) {
    case 'pipeline.preparing_query':
      return 'preparing_query';

    case 'pipeline.started_searching_documentations':
      return 'searching_docs';

    case 'pipeline.searching_web':
      return 'searching_web';

    case 'pipeline.searching_meeting_rooms':
      return 'searching_meeting_rooms';

    case 'pipeline.synthesizing_final_output':
      return 'synthesizing_output';

    case 'response.created':
    case 'response.in_progress':
      return 'thinking'; // "Thinking..." when request accepted, model about to start processing

    case 'response.file_search_call.searching':
      return 'searching'; // "Looking up relevant info…" when model decides to look something up

    case 'response.file_search_call.completed':
      return 'analyzing'; // "Analyzing retrieved content…" once retrieval finished, now reasoning over results

    case 'response.output_text.delta':
      return 'outputting'; // Track that we're outputting text

    case 'response.completed':
      return 'completed'; // Track completion

    // Intermediate events - show analyzing if we've searched, otherwise thinking
    case 'response.output_item.added':
    case 'response.output_item.done':
    case 'response.content_part.added':
      if (eventData?.includes('"web_search_call"')) {
        return 'web_searching';
      }
      return hasSearched ? 'analyzing' : 'thinking';

    // Events during text streaming - track outputting state
    case 'response.output_text.annotation.added':
    case 'response.output_text.done':
    case 'response.content_part.done':
      return 'outputting'; // Track outputting state

    default:
      return 'thinking';
  }
};
